import { type BaseOptionType } from "antd/lib/select"
import { forwardRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { useAppDispatch } from "../../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../../hooks/use-app-selector.ts"
import { type BaseApiResponse } from "../../../../../../../types/api.ts"
import { UNRESOLVED_USER_LABEL } from "../../../../../../../utils/select/entity-labels.ts"
import { getEntityName } from "../../../../../../../utils/user/get-entity-name"
import { SubcontractorFormModal } from "../../../../../../SubcontractorsPage/components/SubcontractorForm/SubcontractorFormModal.tsx"
import { type SubcontractorFormData } from "../../../../../../SubcontractorsPage/components/SubcontractorForm/subcontractor-form-data.ts"
import {
  createInviteSubcontractor,
  createSubcontractor
} from "../../../../../../SubcontractorsPage/redux/subcontractors.thunks.ts"
import { type SubcontractorListItem } from "../../../../../../SubcontractorsPage/types/types.ts"
import { normalize } from "./buildStakeholderOptions.ts"

/** Distinct from any real subcontractor id, which is always server-issued. */
const CREATE_OPTION_VALUE = "__create_subcontractor__"

/**
 * A typed name may be created as a subcontractor on the fly only when it does
 * not already match an existing option. Unlike the Supplier picker, the field
 * is FK-keyed, so a name is compared against option *labels*, never values.
 * Exported for a direct unit test.
 */
export function canCreateSubcontractor(
  typed: string,
  options: BaseOptionType[]
): boolean {
  const name = typed.trim()
  if (!name) return false
  return !options.some(
    (option) => normalize(String(option.label ?? "")) === normalize(name)
  )
}

interface SubcontractorAutoCompleteWithCreateProps {
  subcontractors?: BaseOptionType[]
  placeholder?: string
  value?: string | number | null
  onChange?: (value: string | number | null) => void
  id?: string
}

/**
 * The Contractor field's picker: same discover-and-create offer as
 * `SupplierAutoCompleteWithCreate`, adapted for an FK field rather than a
 * free-text one — see
 * `docs/specs/2026-09-14-subcontractor-inline-create-design.md`. Selecting
 * the create option never writes a value itself; only a saved record's real
 * id does, so cancelling the modal leaves the field exactly as it was.
 *
 * Wrapped in forwardRef because it replaces a bare `<ResolvedSelect>` inside
 * a `Form.Item` — without it, Form.Item's own ref (used to focus/scroll to
 * this field when its required-rule fails) silently lands on nothing.
 */
export const SubcontractorAutoCompleteWithCreate = forwardRef<
  HTMLElement,
  SubcontractorAutoCompleteWithCreateProps
>(function SubcontractorAutoCompleteWithCreate(
  { subcontractors, placeholder, value, onChange, id },
  ref
) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const canCreateSubcontractors = useAppSelector(
    (state) =>
      !!state.auth?.permissions?.codes?.subcontractors?.subcontractors
        ?.subcontractor_create
  )

  const [partySearch, setPartySearch] = useState("")
  const [createdSubcontractorOptions, setCreatedSubcontractorOptions] =
    useState<BaseOptionType[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [pendingName, setPendingName] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const baseOptions = [
    ...createdSubcontractorOptions,
    ...(subcontractors ?? [])
  ]
  const typedName = partySearch.trim()
  const offerCreate =
    canCreateSubcontractors && canCreateSubcontractor(typedName, baseOptions)
  const options = offerCreate
    ? [
        ...baseOptions,
        {
          value: CREATE_OPTION_VALUE,
          label: t("subcontractors.party.addOption", { name: typedName })
        }
      ]
    : baseOptions

  const closeModal = () => {
    setModalOpen(false)
    setPendingName(null)
  }

  const handleSave = (data: SubcontractorFormData) => {
    const { send_invitation, ...rest } = data
    setSaving(true)
    dispatch(
      send_invitation ? createInviteSubcontractor(rest) : createSubcontractor(rest)
    )
      .unwrap()
      .then((response: BaseApiResponse<SubcontractorListItem>) => {
        const created = response.details.data
        const label = getEntityName(created, "") || (pendingName ?? "")
        if (created?.id != null && label) {
          setCreatedSubcontractorOptions((prev) => [
            { value: created.id, label },
            ...prev
          ])
          onChange?.(created.id)
        }
        setPartySearch("")
        closeModal()
      })
      .catch(() => {})
      .finally(() => setSaving(false))
  }

  return (
    <>
      <ResolvedSelect
        ref={ref}
        id={id}
        className="w-full"
        placeholder={placeholder}
        options={options}
        fallbackLabel={UNRESOLVED_USER_LABEL}
        // Explicit `null`, never `undefined`: rc-select treats `undefined` as
        // "uncontrolled" and starts tracking selection in its own internal
        // state — the create option would then visibly "stick" as selected
        // the moment it's clicked, before this component ever gets a chance
        // to intercept it and open the modal instead.
        value={value ?? null}
        showSearch
        allowClear
        onSearch={setPartySearch}
        onChange={(next) => {
          if (offerCreate && next === CREATE_OPTION_VALUE) {
            setPendingName(typedName)
            setModalOpen(true)
            return
          }
          onChange?.((next as string | number | undefined) ?? null)
          // Or the dropdown reopens still offering the stale typed name.
          setPartySearch("")
        }}
        onClear={() => {
          onChange?.(null)
          setPartySearch("")
        }}
        filterOption={(input, option) =>
          option?.value === CREATE_OPTION_VALUE ||
          normalize(String(option?.label ?? "")).includes(normalize(input))
        }
      />
      {modalOpen && (
        <SubcontractorFormModal
          defaultName={pendingName ?? undefined}
          loading={saving}
          onSave={handleSave}
          onCancel={closeModal}
        />
      )}
    </>
  )
})
